import { format } from 'date-fns'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BackButton } from '../../components/button/backButton'
import { Status } from '../../components/cards/statusTag'
import StakeholderFulfilmentCenter from '../../components/common/StakeholderFulfilmentCenter'
import { StakeholderSettlementAccountDetails } from '../../components/common/stakeholderSettlementAccountDetails'
import { StakeholderSettlementConfiguration } from '../../components/common/stakeholderSettlementConfiguration'
import Icon from '../../components/icon'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import Spinner from '../../components/spinner/Spinner'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { formatCurrency } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useDeleteAssignFulfillmentCentreMutation,
  useGetAssignedFulfillmentCentresQuery,
  useGetInvestorQuery,
  useUpdateInvestorMutation,
} from '../../redux-toolkits/investor/investor.slice'
import { IFC, Investor } from '../../redux-toolkits/investor/investor.type'
import {
  useGetFulfilmentCentersQuery,
  useGetSingleVendorsMetricsQuery,
  useGetSingleVendorsSettlementsQuery,
  useUpdateVendorDetailsMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
export const SingleStakeholderPage: React.FC = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedFC, setSelectedFC] = useState<IFC | null>(null)

  const [viewInformation, setViewInformation] = useState(true)
  const [editIndex, setEditIndex] = useState<number>()
  const [uploadedBanner, setUploadedBanner] = useState<File>()
  const [uploadedLogo, setUploadedLogo] = useState<File>()
  const [uploadMode, setUploadMode] = useState('')
  const [businessInformation, setBusinessInformation] = useState<
    {
      key: string
      title: string
      value: string
      type: 'text' | 'textarea'
      placeholder: string
    }[]
  >([])
  const [settlementQuery, setSettlementQuery] = useState<
    QueryProps & { status?: string }
  >({
    limit: NUMBER_OF_ITEMS_PER_PAGE,
    page: 1,
    status: 'all',
  })

  const { id } = useParams()

  const { data: centers, isFetching: fetchingCenters } =
    useGetFulfilmentCentersQuery({})

  const {
    data,
    isFetching: isFetchingFCS,
    refetch: refetchFC,
  } = useGetAssignedFulfillmentCentresQuery({
    id: id ?? '',
  })

  const [updateVendor, { isLoading: updatingVendor }] =
    useUpdateVendorDetailsMutation()

  const [updateSettlementAccount, { isLoading: updatingSettlementAccount }] =
    useUpdateInvestorMutation()

  const {
    data: stakeholder,
    isLoading,
    refetch,
  } = useGetInvestorQuery({
    id: id ?? '',
  })

  const { data: vendorMetrics } = useGetSingleVendorsMetricsQuery({
    id: id ?? '',
    isCustomDateRange: false,
    filter: 'inception_to_date',
  })

  const {
    data: vendorSettlements,
    isError: vendorSettlementFetchError,
    isFetching: fetchingSettlements,
  } = useGetSingleVendorsSettlementsQuery({
    ...settlementQuery,
    id: id ?? '',
    skip: (Number(settlementQuery.page) - 1) * Number(settlementQuery.limit),
    status:
      settlementQuery.status === 'all' ? undefined : settlementQuery.status,
  })

  const navigate = useNavigate()

  const handleUpdateVendor = async (data?: object, callback = () => {}) => {
    const payload = businessInformation.reduce(
      (acc: { [key: string]: string }, { key, value }) => {
        acc[key] = value
        return acc
      },
      {},
    )

    const resp = await updateVendor({
      id: id ?? '',
      data: Object.entries({ ...payload, ...(data ?? {}) }).reduce(
        (acc: Record<string, string>, [key, value]) => {
          if (value !== '') acc[key] = value
          return acc
        },
        {} as Record<string, string>,
      ),
    })

    if (resp.data?.success) {
      callback()
      refetch()
      setEditIndex(undefined)
    }
  }

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  return (
    <Layout>
      {isLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <div className="flex flex-col gap-10">
          <BackButton
            onClick={() => {
              navigate('/settings')
            }}
          />

          <div className="flex flex-col gap-2 text-sec-black">
            <p className="text-[40px] text-black font-recoleta font-semibold">
              {stakeholder?.fullName}
            </p>

            <div>
              <p>Total Revenue</p>
              <p className="text-3xl text-black font-recoleta font-semibold">
                {formatCurrency(vendorMetrics?.data?.totalRevenue ?? 0)}
              </p>
            </div>

            <p>
              Joined{' '}
              {stakeholder?.createdAt
                ? format(stakeholder.createdAt, 'MMM dd, yyyy')
                : ''}
            </p>
            <p>Owned by {stakeholder?.fullName}</p>
            <div className="flex items-center gap-2">
              <p>Stakeholder&apos;s status:</p>
              <Status
                text={stakeholder?.isActive ? 'Active' : 'Inactive'}
                type={stakeholder?.isActive ? 'success' : 'fail'}
              />
            </div>
          </div>

          {/* <CountCardContainer className="!my-0">
            <CountCard count={0} text="Total Revenue" isCurrency={false} />

            <CountCard count={0} text="Total Payout" isCurrency={false} />

            <CountCard
              count={0}
              text="Total Pending Payout"
              isCurrency={false}
            />

            <CountCard count={0} text="Total Vendors" isCurrency={false} />
          </CountCardContainer> */}

          <div className="">
            <button
              className="
              flex items-center
              gap-2 text-lg font-medium
              "
              onClick={() => {
                setViewInformation(!viewInformation)
              }}
              type="button"
            >
              <p>Stakeholder Information</p>

              <Icon
                id="left-caret"
                width={10}
                height={10}
                className={`${
                  viewInformation ? 'rotate-90' : '-rotate-90'
                } transition-all duration-300`}
              />
            </button>

            {viewInformation && (
              <div className="mt-6 flex flex-col gap-4">
                <StakeholderSettlementAccountDetails
                  stakeholder={stakeholder}
                  loading={updatingSettlementAccount}
                  onModify={async (payload) => {
                    if (stakeholder) {
                      const data = {
                        settlementAccount: {
                          ...payload,
                        },
                      }
                      await updateSettlementAccount({
                        body: data,
                        id: stakeholder?._id,
                      })
                    }
                  }}
                  className="pb-4 border-b"
                />

                <StakeholderFulfilmentCenter
                  stakeholderId={stakeholder?._id ?? ''}
                  centers={
                    centers?.data?.docs?.map((item) => ({
                      name: item.name,
                      value: item._id,
                    })) ?? []
                  }
                  fcs={data ?? []}
                  isFetching={isFetchingFCS}
                  selectedFC={selectedFC}
                  setSelectedFC={setSelectedFC}
                  onSuccess={() => {
                    refetchFC()
                  }}
                  setOpenDeleteModal={setOpenDeleteModal}
                />

                <StakeholderSettlementConfiguration
                  stakeholder={stakeholder}
                  className="pb-4 border-b mt-4"
                  refetch={() => {
                    refetch()
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <ActivateInvestorMemberModal
        stakeholder={stakeholder}
        selectedFC={selectedFC}
        open={openDeleteModal}
        closeModal={() => {
          setOpenDeleteModal(false)
          setSelectedFC(null)
          refetchFC()
        }}
      />
    </Layout>
  )
}

const ActivateInvestorMemberModal: React.FC<{
  open: boolean
  closeModal: () => void
  stakeholder?: Investor
  selectedFC: IFC | null
}> = ({ open, closeModal, stakeholder, selectedFC }) => {
  const [deleteFC, { isLoading }] = useDeleteAssignFulfillmentCentreMutation()

  return (
    <>
      <ConfirmationModal
        loading={isLoading}
        open={open}
        setOpen={() => {}}
        onCloseCallback={() => {
          closeModal()
        }}
        title="Delete Stakeholder"
        message={`Are you sure you want to delete ${stakeholder?.fullName}?`}
        onConfirm={async () => {
          if (!stakeholder || !selectedFC) return

          const res = await deleteFC({
            id: stakeholder._id,
            fcId: selectedFC?.fulfilmentCentre?._id,
          })

          if (res.data) {
            closeModal()
          }
        }}
        confirmText="Delete"
      />
    </>
  )
}
